import { useState } from "react"
import { useTranslation } from "react-i18next"
import { XMarkIcon } from '@heroicons/react/24/outline'
import { QRCodeSVG } from 'qrcode.react'
import { Form } from "./Form"
import { Button } from "./Button"
import { getDeliveryPassword } from "../services/orderServices"

export const DeliveryPasswordModal = ({isOpen, onClose, orderId, shippingCountry, storeWarehouseCountry}) => {
    const { i18n } = useTranslation()
    const [securityValidationFieldValues, setSecurityValidationFieldValues] = useState({email: "", tax_id: ""})
    const [waitingValidation, setWaitingValidation] = useState(false)
    const [validationError, setValidationError] = useState(false)
    const [deliveryPasswordData, setDeliveryPasswordData] = useState([])
    const [showPassword, setShowPassword] = useState(false)

    const hasMultiplePackages = deliveryPasswordData && deliveryPasswordData.length > 1

    const buildSecurityValidationFieldsData = () => {
        const fields = [
            {
                key: "email",
                label: i18n.t("delivery_password.security_validation.fields.email"),
                type: "text"
            }
        ]

        if (storeWarehouseCountry === "BR" && shippingCountry === "BR") {
            fields.push({
                key: "tax_id",
                label: i18n.t("delivery_password.security_validation.fields.tax_id"),
                type: "text"
            })
        }

        return fields
    }

    const handleSecurityValidationChange = (fieldKey, newValue) => {
        if (securityValidationFieldValues.hasOwnProperty(fieldKey)) {
            const newSecurityValidationFieldValues = { ...securityValidationFieldValues }
            newSecurityValidationFieldValues[fieldKey] = newValue
            setSecurityValidationFieldValues(newSecurityValidationFieldValues)
        }
    }

    const handleSecurityValidationSubmit = async () => {
        setWaitingValidation(true)
        setValidationError(false)

        const dataContent = { shipping_email_response: securityValidationFieldValues.email }

        if (storeWarehouseCountry === "BR" && shippingCountry === "BR") {
            dataContent.billing_tax_id_response = securityValidationFieldValues.tax_id
        }

        try {
            const res = await getDeliveryPassword(orderId, dataContent)
            setDeliveryPasswordData(res)
            setShowPassword(true)
        } catch (error) {
            if (error.response?.status === 422) {
                setValidationError(true)
            } else {
                setValidationError(true)
            }
        } finally {
            setWaitingValidation(false)
        }
    }

    const handleClose = () => {
        setDeliveryPasswordData([])
        setShowPassword(false)
        setValidationError(false)
        setSecurityValidationFieldValues({ email: "", tax_id: "" })
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">
                        {i18n.t("delivery_password.title")}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-1 hover:bg-gray-100 rounded-full"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {!showPassword ? (
                    <div>
                        <p className="text-gray-600 mb-4">
                            {hasMultiplePackages
                                ? i18n.t("delivery_password.security_validation.multiple_packages_description")
                                : i18n.t("delivery_password.security_validation.description")
                            }
                        </p>

                        <Form
                            fieldsData={buildSecurityValidationFieldsData()}
                            inputs={securityValidationFieldValues}
                            handleInputUpdate={handleSecurityValidationChange}
                            onSubmit={handleSecurityValidationSubmit}
                            disableSubmit={waitingValidation}
                            errors={{serverError: validationError ? i18n.t("delivery_password.security_validation.validation_failed") : null}}
                            submitData={{
                                label: waitingValidation ?
                                    i18n.t("delivery_password.security_validation.validating_submit") :
                                    i18n.t("delivery_password.security_validation.submit_button")
                            }}
                        />
                    </div>
                ) : (
                    <div>
                        <p className="text-gray-600 mb-4">
                            {hasMultiplePackages 
                                ? i18n.t("delivery_password.multiple_packages_description")
                                : i18n.t("delivery_password.description")
                            }
                        </p>

                        <div className="space-y-6">
                            {deliveryPasswordData.map((packageData, index) => (
                                <div key={packageData.shipping_number || index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    {hasMultiplePackages && (
                                        <div className="mb-4">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                                {i18n.t("delivery_password.package_label", { number: index + 1 })}
                                            </h3>
                                        </div>
                                    )}
                                    {packageData.shipping_number && (
                                        <div className={hasMultiplePackages ? "mb-4" : "mb-4"}>
                                            <p className="text-sm text-gray-600 mb-3">
                                                {i18n.t("delivery_password.shipping_number_label")}: {packageData.shipping_number}
                                            </p>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        {(packageData.delivery_password || packageData.pickup_short_code) && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {packageData.pickup_short_code
                                                        ? i18n.t("delivery_password.pickup_short_code_label")
                                                        : i18n.t("delivery_password.password_label")}
                                                </label>
                                                <div className="p-3 bg-white border border-gray-200 rounded-md">
                                                    <code className="text-lg font-mono font-bold text-gray-800">
                                                        {packageData.delivery_password || packageData.pickup_short_code}
                                                    </code>
                                                </div>
                                            </div>
                                        )}

                                        {(packageData.delivery_password_image_url || packageData.pickup_qr_value) && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {i18n.t("delivery_password.qr_code_label")}
                                                </label>
                                                <div className="flex justify-center">
                                                    {packageData.delivery_password_image_url ? (
                                                        <img
                                                            src={packageData.delivery_password_image_url}
                                                            alt={`QR Code for package ${index + 1}`}
                                                            className="max-w-full h-auto border border-gray-200 rounded-md"
                                                        />
                                                    ) : (
                                                        <div
                                                            className="p-4 bg-white border border-gray-200 rounded-md"
                                                            data-testid={`pickup-qr-${index + 1}`}
                                                        >
                                                            <QRCodeSVG
                                                                value={packageData.pickup_qr_value}
                                                                size={192}
                                                                title={`QR Code for package ${index + 1}`}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex gap-3">
                            <Button
                                label={i18n.t("delivery_password.close_button")}
                                onClick={handleClose}
                                className="flex-1 bg-gray-500 hover:bg-gray-600"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
